import {
    DTOCardFormField, NestedField,
} from 'app/types/form.types'
import {
    LeaseType, LocationType, AddressMainCategory,
    DeliveryServiceType, OrderTransportModeRoad,
    PositioningSelection, DeliveryOptionRoad,
} from 'app/enums/label.enums'

import {
    Order,
} from '../../../orders.types'
import {
    PositioningSetupType,
} from '../orderConfirmation.types'

type FormFields = PositioningSetupType & Order

const getFields = ({
    transportModeForwarder,
    forwarderPositioningId,
    forwarderPositioningSelection,
    getAllowedValues,
    isDeliveryRoad,
    leaseType,
    order,
    isOtherByDefault,
    deliveryOptionRoad,
}: {
    transportModeForwarder: OrderTransportModeRoad,
    getAllowedValues: (enumType: any) => Record<string, string>,
    isDeliveryRoad: boolean,
    forwarderPositioningId: number,
    forwarderPositioningSelection: PositioningSelection,
    leaseType: LeaseType,
    order: Order,
    isOtherByDefault: boolean,
    deliveryOptionRoad: DeliveryOptionRoad,
}): DTOCardFormField<keyof FormFields>[] => {
    const transportModeForwarderField: NestedField<keyof PositioningSetupType>[] = [{
        name: 'transportModeForwarder',
        componentName: 'EnumSelector',
        allowedValues: getAllowedValues(OrderTransportModeRoad),
        labelKey: 'Order.transportModeForwarder',
    }]

    const transportModeRoadField: NestedField<keyof PositioningSetupType>[] = [{
        name: 'transportModeRoad',
        componentName: 'EnumSelector',
        allowedValues: getAllowedValues(OrderTransportModeRoad),
        labelKey: 'Order.transportModeRoad',
    }]

    const freightForwarderFields: NestedField<keyof PositioningSetupType>[] = [
        {
            name: 'freightForwarder',
            componentName: 'CompanyObjectSelector',
            required: true,
            labelKey: 'OrderByNumberView.freightForwarder',
        },
        {
            name: 'additionalCommentFreightForwarder',
            componentName: 'InputSingleline',
            disabled: true,
            labelKey: 'OrderByNumberView.additionalCommentFreightForwarder',
        },
    ]

    const numberOfVansTracksFields: NestedField<keyof PositioningSetupType>[] = [
        {
            name: 'deliveryOptionRoad',
            componentName: 'EnumSelector',
            allowedValues: getAllowedValues(DeliveryOptionRoad),
            required: true,
            className: 'deliveryOptionRoad',
            labelKey: 'OrderStep.deliveryOptionRoad',
        },
        ...deliveryOptionRoad === DeliveryOptionRoad.TRUCKS ? [{
            name: 'numberOfTrucks',
            componentName: 'IntegerInput',
            className: 'numberOfVehicles',
            required: true,
            labelKey: 'OrderStep.numberOfTrucks',
        }] as NestedField<keyof PositioningSetupType>[] : [],
        ...deliveryOptionRoad === DeliveryOptionRoad.VANS ? [{
            name: 'numberOfVans',
            componentName: 'IntegerInput',
            className: 'numberOfVehicles',
            required: true,
            labelKey: 'OrderStep.numberOfVans',
        }] as NestedField<keyof PositioningSetupType>[] : [],
    ]

    const forwarderFields: NestedField<keyof PositioningSetupType>[] = [
        {
            name: 'forwarderPositioningSelection',
            componentName: 'EnumSelector',
            noSort: true,
            allowedValues: getAllowedValues(PositioningSelection),
            labelKey: 'CustomerTransportCreateDto.fwdPosSelection',
        },
        {
            name: 'forwarderPositioning',
            componentName: 'LocationObjectSelector',
            filterAddressMainCategory: AddressMainCategory.SERVICECENTER,
            disabled: !isDeliveryRoad
            || forwarderPositioningSelection !== PositioningSelection.OTHER,
            required: true,
            labelKey: 'LaneOpsInformationDto.fwdPos',
        },
        ...forwarderPositioningSelection === PositioningSelection.OTHER ? [{
            name: 'forwarderPositioningContacts',
            componentName: 'MultipleContactByLocationObjectSelector',
            existedForm: isOtherByDefault,
            required: true,
            selectedLocation: forwarderPositioningId,
            labelKey: 'LaneOpsInformationDto.fwdPosContacts',
        }] as NestedField<keyof PositioningSetupType>[] : [{
            name: 'forwarderPositioningContacts',
            componentName: 'MultipleContactObjectSelector',
            disabled: true,
            labelKey: 'LaneOpsInformationDto.fwdPosContacts',
        }] as NestedField<keyof PositioningSetupType>[],
    ]

    return [
        {
            id: 'positioningSetup',
            className: 'positioningSetup',
            title: 'General',
            fields: [
                {
                    id: 'column_1',
                    fields: [
                        {
                            name: 'originAirport',
                            componentName: 'LocationObjectSelector',
                            filterLocationType: LocationType.AIRPORT,
                            value: order?.originAirport,
                            disabled: true,
                            labelKey: 'Order.originAirport',
                        },
                        {
                            name: 'destinationAirport',
                            componentName: 'LocationObjectSelector',
                            filterLocationType: LocationType.AIRPORT,
                            value: order?.destinationAirport,
                            disabled: true,
                            labelKey: 'Order.destinationAirport',
                        },
                        {
                            name: 'leaseType',
                            componentName: 'EnumSelector',
                            allowedValues: getAllowedValues(LeaseType),
                            disabled: true,
                            labelKey: 'Order.leaseType',
                        },
                        {
                            name: 'orderReceived',
                            componentName: 'DateTimeZonedSelect',
                            required: true,
                            labelKey: 'Order.orderReceived',
                        },
                    ],
                },
                {
                    id: 'column_2',
                    fields: [
                        {
                            name: 'deliveryServiceType',
                            componentName: 'EnumSelector',
                            allowedValues: getAllowedValues(DeliveryServiceType),
                            disabled: true,
                            labelKey: 'Order.deliveryServiceType',
                        },
                        ...isDeliveryRoad ? transportModeRoadField : [],
                        ...isDeliveryRoad ? transportModeForwarderField : [],
                        ...transportModeForwarder === OrderTransportModeRoad.FTL && isDeliveryRoad
                            ? numberOfVansTracksFields
                            : [],
                        ...isDeliveryRoad ? [
                            {
                                name: 'estimatedPickup',
                                componentName: 'DateTimeZonedSelect',
                                labelKey: 'CustomerTransportCreateDto.estimatedPickup',
                            },
                            {
                                name: 'requestedPickup',
                                componentName: 'DateTimeZonedSelect',
                                labelKey: 'OrderStep.requestedPickup',
                            },
                        ] as NestedField<keyof PositioningSetupType>[] : [],
                    ],
                },
                {
                    id: 'column_3',
                    fields: [
                        ...isDeliveryRoad ? forwarderFields : [],
                        ...leaseType === LeaseType.AIRLINE_LEASE
                            ? freightForwarderFields
                            : [],
                    ],
                },
                {
                    id: 'column_4',
                    fields: [
                        {
                            name: 'mawbNumber',
                            componentName: 'InputSingleline',
                            labelKey: 'Order.mawbNumber',
                        },
                        {
                            name: 'hawbNumber',
                            componentName: 'InputSingleline',
                            labelKey: 'CustomerTransportCreateDto.hawbNumber',
                        },
                        {
                            name: 'customerReference',
                            componentName: 'InputSingleline',
                            labelKey: 'Order.customerReference',
                        },
                        {
                            name: 'personInCharge',
                            componentName: 'InputSingleline',
                            labelKey: 'Order.personInCharge',
                        },
                    ],
                },
            ],
        },
        {
            id: 'requiredContainers',
            className: 'requiredContainers',
            title: 'Required Containers',
            fields: [
                {
                    id: 'column_requiredContainers_1',
                    fields: [{
                        name: 'requiredContainers',
                        componentName: 'RequiredContainerByProductTypeList',
                        allowedProductTypes: order?.allowedProductTypes,
                        value: order?.requiredContainers,
                        disabled: true,
                        labelKey: 'OrderByNumberView.requiredContainers',
                    }],
                },
                {
                    id: 'column_requiredContainers_2',
                    fields: [{
                        name: 'allowedProductTypes',
                        componentName: 'AllowedContainerTypes',
                        value: order?.allowedProductTypes,
                        noLabel: true,
                    }],
                },
                {
                    id: 'column_requiredContainers_3',
                    fields: [{
                        name: 'requestedHandover',
                        componentName: 'DateTimeSelect',
                        value: order?.requestedHandover,
                        disabled: true,
                        labelKey: 'OrderByNumberView.requestedHandover',
                    }],
                },
                {
                    id: 'column_requiredContainers_4',
                    fields: [{
                        name: 'leaseStartTimestamp',
                        componentName: 'DateTimeZonedSelect',
                        value: order?.billing.leaseStartTimestamp,
                        disabled: true,
                        labelKey: 'CustomerTransportCreateDto.leaseStartTimestamp',
                    }],
                },
            ],
        },
    ]
}

export default getFields
